import { useEffect, useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { config } from 'services/config';
import { useCreateLogItemMutation } from '@/state/slices/logItem';
import { ActionTypeChoices, LogItem, LogItemParams } from '@/models/logItem';
import { buildQueryString } from '@/state/slices/index.ts';

export function useAuditFilters() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);

  const [dateRange, setDateRange] = useState<[string, string]>([
    dayjs().subtract(30, 'day').startOf('day').toISOString(),
    dayjs().endOf('day').toISOString(),
  ]);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [objectType, setObjectType] = useState<string | undefined>(undefined);
  const [objectId, setObjectId] = useState<string | undefined>(undefined);

  const [isExporting, setIsExporting] = useState(false);

  const [createLogItem] = useCreateLogItemMutation();

  useEffect(() => {
    setPage(1);
  }, [dateRange, userId, actionTypes, objectType, objectId]);

  const params = useMemo<LogItemParams>(
    () => ({
      page,
      size,
      createdAtAfter: dateRange[0],
      createdAtBefore: dateRange[1],
      userId: userId || undefined,
      actionType:
        actionTypes.length > 0
          ? (actionTypes as LogItemParams['actionType'])
          : undefined,
      objectType: (objectType as LogItemParams['objectType']) || undefined,
      objectId: objectId || undefined,
    }),
    [page, size, dateRange, userId, actionTypes, objectType, objectId]
  );

  const onTableChange: TableProps<LogItem>['onChange'] = (pagination) => {
    setPage(pagination?.current ?? page);
    setSize(pagination?.pageSize ?? size);
  };

  const onExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const filterParams: LogItemParams = {
        createdAtAfter: dateRange[0],
        createdAtBefore: dateRange[1],
        userId: userId || undefined,
        actionType:
          actionTypes.length > 0
            ? (actionTypes as LogItemParams['actionType'])
            : undefined,
        objectType: (objectType as LogItemParams['objectType']) || undefined,
        objectId: objectId || undefined,
      };
      const qs = buildQueryString(filterParams as Record<string, unknown>);
      const token = localStorage.getItem('AccessToken');
      const response = await fetch(`${config.BASE_URL}log-items/export/${qs}`, {
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit_log.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 0);

      await createLogItem({ actionType: ActionTypeChoices.AUDIT_LOG_EXPORTED });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    params,
    onTableChange,
    isExporting,
    onExportCsv,

    dateRange,
    onDateRangeChange: setDateRange,
    userId,
    onUserChange: setUserId,
    actionTypes,
    onActionTypesChange: setActionTypes,
    objectType,
    onObjectTypeChange: setObjectType,
    objectId,
    onObjectIdChange: setObjectId,
  };
}
